/** The frame of every data place: a header card, a strip of tiles, one chart, one line of filters and
 * one table - ADR-0212 decision 2.
 *
 * **It renders nothing but its slots.** There is no `children` prop and no place to put a paragraph:
 * the type refuses one, and `TablePage.test.tsx` renders a stray child and asserts it does not reach
 * the document. A page that wants to explain itself puts the explanation behind an `InfoPopover` or a
 * `HowDrawer`; a page that wants to say its state (empty, failed, blocked) passes `state`, which
 * *replaces the table* in the table card. The text-budget guard (`textBudget.ts`) then has one thing
 * to hold a screen to, and the slots are why it can.
 *
 * Slots, top to bottom:
 * - the **header card**: the place's icon, its title (the page's `<h1>`, claimed from the shell by
 *   `PageTitle`), a small label (the tab or the kind), and `actions` on the right - `AddImportActions`
 *   for a data place;
 * - `band`: the dashboard band (`DashboardBand`), KPI tiles with a change and a small line over two
 *   or three small charts - a slot, the charts are drawn by the page's own package;
 * - `tiles`: a `TileStrip` of three to five tiles, for a page with no band;
 * - `extra`: one control or card of the place's own that is neither a tile nor a chart - Upload's
 *   drop zone. A slot like the others: it takes a component, and a page still cannot add a paragraph
 *   beside it because there is no `children`;
 * - `chart`: at most one chart card;
 * - the **table card**: `filters` (a `FilterLine`) over the `table` (a `GridTable`), or `state` in the
 *   table's place.
 *
 * **Charts are collapsed by default** (`charts.ts`): the `band`'s charts and the `chart` card show only
 * while the Charts toggle at the right end of the filter line is open, and the choice is remembered. The
 * tiles are always shown.
 *
 * The page scrolls inside itself (the shell's rule that the document never does); the table card
 * takes the room that is left and its grid scrolls within it, with its header held at the top.
 */

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import type { ChartsSlot } from "./charts";
import { ChartsContext, readChartsOpen, writeChartsOpen } from "./charts";
import { useScrollableRegionFocus } from "../useScrollableRegionFocus";
import { PageHeaderCard } from "./PageHeaderCard";
import styles from "./TablePage.module.css";

export interface TablePageProps {
  icon: Icon;
  /** The place's name, and the page's `<h1>`. */
  title: string;
  /** A small label beside the title: the tab, or what kind of thing the rows are. */
  label?: string;
  actions?: ReactNode;
  /** The dashboard band between the header card and the filter line: a `DashboardBand` of KPI tiles
   * and two or three small charts. The slot only; the charts are the owning package's. */
  band?: ReactNode;
  tiles?: ReactNode;
  extra?: ReactNode;
  chart?: ReactNode;
  filters?: ReactNode;
  table?: ReactNode;
  /** Empty, failed or blocked: replaces the table. One plain sentence at most. */
  state?: ReactNode;
  /** Cards that follow the table card, for a page whose table is the first thing to read and whose
   * figures and facts come after it (the filing page: its to-do list sits directly under the
   * readiness figure and path). A slot like the others: it takes components, never a paragraph. */
  after?: ReactNode;
  /** The table card is as tall as its rows (up to 70% of the window, then the grid scrolls inside it)
   * instead of taking only the room that is left. For a page with cards after the table: the filing
   * page's to-do list showed five rows and a cut-off sixth with the figures pushed below it. */
  tableFitsRows?: boolean;
  /** There is nothing to draw: no row, so no chart either. The Charts toggle is not drawn and a chart
   * card is not shown - an empty chart reads as a result, and a toggle for nothing is a control that
   * opens nothing (KD, 2026-11-02). The tiles stay: they say "no figure yet" with a dash. */
  nothingYet?: boolean;
}

export function TablePage({
  icon: Glyph,
  title,
  label,
  actions,
  band,
  tiles,
  extra,
  chart,
  filters,
  table,
  state,
  after,
  tableFitsRows = false,
  nothingYet = false,
}: TablePageProps) {
  const body = state ?? table;
  // The page scrolls inside itself. A page of cards with no control in them (a `state`, a read-only
  // list) would be a scroll area no keyboard can reach: it becomes a named region when it overflows.
  const frame = useRef<HTMLDivElement>(null);
  useScrollableRegionFocus(frame, { label: `${title} page` });
  
  const [chartsOpen, setChartsOpen] = useState(readChartsOpen);
  const toggleCharts = useCallback(() => {
    setChartsOpen((open) => {
      writeChartsOpen(!open);
      return !open;
    });
  }, []);
  const hasCharts = !nothingYet && (band !== undefined || chart !== undefined);
  // Nothing to chart: the band's charts stay shut whatever the remembered choice is.
  const slot = useMemo<ChartsSlot>(
    () => ({ available: hasCharts, open: chartsOpen && !nothingYet, toggle: toggleCharts }),
    [hasCharts, chartsOpen, nothingYet, toggleCharts],
  );
  return (
    <ChartsContext.Provider value={slot}>
    <div ref={frame} className={styles.page} data-table-page>
      <PageHeaderCard icon={Glyph} title={title} label={label} actions={actions} />
      {band}
      {tiles}
      {extra}
      {chart && chartsOpen && !nothingYet ? (
        <section className={`card ${styles.chart}`} aria-label={`${title} chart`} data-chart>
          {chart}
        </section>
      ) : null}
      {filters || body ? (
        <section
          className={`card ${styles.tableCard}`}
          aria-label={`${title} table`}
          data-state={state !== undefined ? "" : undefined}
          data-fits-rows={tableFitsRows ? "" : undefined}
        >
          {filters}
          <div className={styles.body}>{body}</div>
        </section>
      ) : null}
      {after}
    </div>
    </ChartsContext.Provider>
  );
}
